import React, { useEffect, useState } from 'react';
import styles from './styles.module.scss';
import {
  Button,
  Card,
  Col,
  Form,
  Input,
  Modal,
  notification,
  Pagination,
  Row,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
} from 'antd';
import { DeleteOutlined, SendOutlined } from '@ant-design/icons';
import { REQUIRED } from '../../constants/rule-form';
import { ROLE_USER } from '../../constants/common';
import CustomButton from '../../components/custom-button';
import PageTitle from '../../components/page-title';
import AxiosUtils from '../../utils/axios-utils';
import { API } from '../../service/api-route';
import { ColumnsType } from 'antd/lib/table';
import { shortenName } from '../../utils/short-name';
import { formatedDate } from '../../utils/common';
import { UserFilter } from '../news-manager';

const LIMIT = 10;

const initFilter = { page: 1, limit: LIMIT };

interface DataPost {
  title: string;
  content: string;
  roles: any;
}

// Giá trị gửi lên server vẫn là mã vai trò; chỉ đổi chữ hiển thị.
const ROLE_OPTIONS = [
  { value: ROLE_USER.USER, label: 'Người dùng (USER)' },
  { value: ROLE_USER.MOD, label: 'Quản lý (MOD)' },
  { value: ROLE_USER.AGENCY, label: 'Đối tác (AGENCY)' },
  { value: ROLE_USER.CREATOR, label: 'Creator (CREATOR)' },
];

const roleLabel = (role: string) =>
  ROLE_OPTIONS.find((option) => option.value === role)?.label ?? role;

function NotificationManager() {
  const [form] = Form.useForm();
  const [modal, modalContextHolder] = Modal.useModal();
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [dataResponse, setDataResponse] = useState<any>({
    items: [],
    totalCount: 0,
  });
  const [isModalDeleteOpen, setIsModalDeleteOpen] = useState(false);
  const [filter, setFilter] = useState(initFilter as UserFilter);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [statusUpdateData, setStatusUpdateData] = useState(false);
  const previewTitle = Form.useWatch('title', form);
  const previewContent = Form.useWatch('content', form);

  const handleChangePage = (page: number) => {
    setFilter((preState: any) => ({ ...preState, page }));
  };

  const sendNotification = async (values: DataPost) => {
    setSending(true);
    try {
      await AxiosUtils.post(API.pushNotification, { ...values, type: 'COMBAT', isSave: true });
      notification.success({
        message: 'Thành công',
        description: 'Gửi thông báo thành công!',
        duration: 4,
      });
      form.resetFields();
      onChangeStatusUpdateData();
    } catch (error) {
      notification.error({
        message: 'Không thành công',
        description: 'Có lỗi xảy ra, vui lòng thử lại.',
        duration: 4,
      });
    } finally {
      setSending(false);
    }
  };

  // Gửi tới mọi tài khoản của các nhóm đã chọn và không thu hồi được → hỏi lại một lần.
  const onFinish = (values: DataPost) => {
    const groups = (values.roles || []).map((role: string) => roleLabel(role)).join(', ');
    modal.confirm({
      title: 'Gửi thông báo ngay?',
      content: `Thông báo sẽ gửi tới mọi tài khoản thuộc: ${groups}. Đã gửi thì không thu hồi được.`,
      okText: 'Gửi',
      cancelText: 'Hủy',
      onOk: () => sendNotification(values),
    });
  };

  useEffect(() => {
    const fetchNotifications = async () => {
      setLoading(true);
      try {
        const res: any = await AxiosUtils.get(API.adminNotification, {
          ...filter,
        });
        setDataResponse({ items: res?.data?.data, totalCount: res?.data?.metadata?.total });
      } finally {
        setLoading(false);
      }
    };

    fetchNotifications();
  }, [filter, statusUpdateData]);

  const onSelectChange = (newSelectedRowKeys: React.Key[]) => {
    setSelectedRowKeys(newSelectedRowKeys);
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
  };
  const hasSelected = selectedRowKeys.length > 0;

  const columns: ColumnsType<any> = [
    {
      title: 'ID',
      dataIndex: 'id',
      width: 72,
      key: 'id',
    },
    // Bảng dàn cố định (tableLayout="fixed"): Tiêu đề rộng cố định, Nội dung co giãn; chữ
    // dài — kể cả link không dấu cách — xuống dòng rồi cắt "…" trong ô, không đẩy cột khác.
    {
      title: 'Tiêu đề',
      dataIndex: 'title',
      key: 'title',
      width: 220,
      render: (value) => (
        <Tooltip title={shortenName(value || '', 300)}>
          <span className={styles.clamp}>{value || ''}</span>
        </Tooltip>
      ),
    },
    {
      title: 'Nội dung',
      dataIndex: 'content',
      key: 'content',
      render: (value) => (
        <Tooltip title={shortenName(value || '', 300)}>
          <span className={styles.clamp}>{value || ''}</span>
        </Tooltip>
      ),
    },
    {
      title: 'Gửi tới',
      dataIndex: 'group',
      key: 'group',
      // Thẻ dài nhất "Creator (CREATOR)" ~132px + 2 × 16px đệm ô.
      width: 172,
      render: (value) =>
        value ? (
          <Space size={[4, 4]} wrap>
            {String(value)
              .split(',')
              .filter(Boolean)
              .map((role) => (
                <Tag key={role} style={{ marginInlineEnd: 0 }}>
                  {roleLabel(role.trim())}
                </Tag>
              ))}
          </Space>
        ) : (
          ''
        ),
    },

    {
      title: 'Ngày gửi',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 120,
      render: (value) => <span className={styles.nowrap}>{formatedDate(value)}</span>,
    },
  ];

  const handelOpenDeleteModal = () => {
    setIsModalDeleteOpen(true);
  };

  const onChangeStatusUpdateData = () => setStatusUpdateData((prev) => !prev);

  const handleCancelDeleteModal = () => {
    setIsModalDeleteOpen(false);
  };

  const handleOkDeleteModal = async () => {
    setLoading(true);
    try {
      await AxiosUtils._delete(API.deleteNotification, { ids: selectedRowKeys });
      onChangeStatusUpdateData();
      notification.success({
        message: 'Thành công',
        description: 'Xóa thành công!',
        duration: 4,
      });
      setSelectedRowKeys([]);
    } catch (error) {
      notification.error({
        message: 'Không thành công',
        description: 'Có lỗi xảy ra, vui lòng thử lại.',
        duration: 4,
      });
    } finally {
      setLoading(false);
      setIsModalDeleteOpen(false);
    }
  };

  return (
    <div>
      {modalContextHolder}
      <PageTitle title="Gửi thông báo" />

      <Row gutter={[16, 16]} className={styles.composeRow}>
        <Col xs={24} xl={14}>
          <Card title="Soạn thông báo đẩy" styles={{ body: { padding: 20 } }}>
            <Form name="form" form={form} layout="vertical" onFinish={onFinish} autoComplete="off">
              <Form.Item
                label="Tiêu đề"
                name="title"
                rules={[
                  REQUIRED,
                  {
                    max: 200,
                    message: 'Không được quá 200 kí tự',
                  },
                ]}
              >
                <Input placeholder="Nhập tiêu đề" size="large" />
              </Form.Item>

              <Form.Item
                label="Nội dung"
                rules={[{ required: true, message: 'Nội dung này bắt buộc' }]}
                name="content"
              >
                <Input.TextArea placeholder="Nhập nội dung" showCount maxLength={300} rows={4} />
              </Form.Item>

              <Form.Item
                label="Gửi tới"
                extra="Mọi tài khoản thuộc các nhóm đã chọn nhận thông báo trên điện thoại và trong mục Thông báo của app."
                rules={[REQUIRED]}
                name="roles"
              >
                <Select
                  mode="multiple"
                  size="large"
                  placeholder="Chọn nhóm người dùng nhận thông báo"
                  options={ROLE_OPTIONS}
                />
              </Form.Item>

              <CustomButton
                bgColor="secondary-green"
                text="Gửi thông báo"
                icon={<SendOutlined />}
                htmlType="submit"
                loading={sending}
              />
            </Form>
          </Card>
        </Col>
        <Col xs={24} xl={10}>
          <Card title="Xem trước" styles={{ body: { padding: 20 } }}>
            <div className={styles.phone}>
              <div className={styles.push}>
                <div className={styles.pushHead}>
                  <span className={styles.pushApp}>HP Media</span>
                  <span>bây giờ</span>
                </div>
                <div className={styles.pushTitle}>{previewTitle || 'Tiêu đề thông báo'}</div>
                <div className={styles.pushBody}>
                  {previewContent || 'Nội dung thông báo sẽ hiện ở đây.'}
                </div>
              </div>
            </div>
            <p className={styles.hint}>
              Hình minh hoạ — điện thoại có thể cắt bớt tiêu đề/nội dung dài.
            </p>
          </Card>
        </Col>
      </Row>

      <div className={styles.sectionHeader}>
        <h2 className={styles.sectionTitle}>Đã gửi</h2>
        {hasSelected && (
          <Button danger icon={<DeleteOutlined />} onClick={handelOpenDeleteModal}>
            Xóa {selectedRowKeys.length} thông báo
          </Button>
        )}
      </div>
      <Card
        className="custom-table"
        styles={{ body: { padding: 0 } }}
        style={{ overflow: 'hidden' }}
      >
        <Table
          rowSelection={rowSelection}
          columns={columns}
          rowKey="id"
          pagination={false}
          dataSource={dataResponse.items}
          tableLayout="fixed"
          // Bảng trống + scroll.x: antd đặt ô "Trống" rộng bằng cả khung (tính cả viền 1px
          // hai bên) nên bảng rộng hơn khung 2px và hiện thanh cuộn ngang thừa. Chỉ cuộn khi có dòng.
          // 840 = các cột cố định (~630) + tối thiểu ~200px cho cột Nội dung.
          scroll={dataResponse.items?.length ? { x: 840 } : undefined}
          loading={loading}
        />
        {dataResponse.totalCount > LIMIT && (
          <div className={styles.pagination}>
            <Pagination
              showSizeChanger={false}
              current={filter.page}
              onChange={handleChangePage}
              showLessItems
              total={dataResponse.totalCount}
              pageSize={LIMIT}
            />
          </div>
        )}
      </Card>
      <Modal
        title="Xóa thông báo"
        open={isModalDeleteOpen}
        onOk={handleOkDeleteModal}
        onCancel={handleCancelDeleteModal}
        okText="Xóa"
        cancelText="Hủy"
        okButtonProps={{ danger: true }}
        confirmLoading={loading}
      >
        <p>Bạn có chắc muốn xóa {selectedRowKeys.length} thông báo đã chọn?</p>
      </Modal>
    </div>
  );
}

export default NotificationManager;
